import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney, getDisplayPrice } from '../../util/currency';
import { useLivePrice } from '../../util/useLivePrice';
import { types as sdkTypes } from '../../util/sdkLoader';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';

import css from './ListingCard.module.css';

const { Money } = sdkTypes;

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { listing, price, publicData, config, intl, listingTypeConfig } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  // Live NOK price using current exchange rate, falls back to listing.price
  // when no override is needed or rates aren't ready yet.
  const livePrice = useLivePrice(listing, config.currency);

  // Check if this is an auction listing
  const isAuction = publicData?.isAuction === true;
  const auctionEstimateLow = publicData?.auctionEstimateLow;
  const auctionEstimateHigh = publicData?.auctionEstimateHigh;

  // Check if this is a service category listing (no fixed price)
  const isServiceCategory = publicData?.categoryLevel1 === 'relatertetjenester';

  // For service category listings, show contact message
  if (isServiceCategory) {
    return (
      <div className={css.price}>
        <span className={css.contactForQuote}>
          <FormattedMessage id="ListingCard.contactForQuote" />
        </span>
      </div>
    );
  }

  // For auction listings, show estimate range
  if (isAuction && auctionEstimateLow && auctionEstimateHigh) {
    // Auction estimates are stored in the seller's chosen currency
    const currency = publicData?.listingCurrency || price?.currency || config.currency;
    const lowEstimateMoney = new Money(auctionEstimateLow, currency);
    const highEstimateMoney = new Money(auctionEstimateHigh, currency);
    const formattedLow = formatMoney(intl, lowEstimateMoney);
    const formattedHigh = formatMoney(intl, highEstimateMoney);

    return (
      <div className={css.price}>
        <span className={css.auctionEstimate}>
          <FormattedMessage
            id="ListingCard.auctionEstimate"
            values={{ lowEstimate: formattedLow, highEstimate: formattedHigh }}
          />
        </span>
      </div>
    );
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);

  // Show NOK (marketplace currency) price using live exchange rate. Display
  // the seller's original currency as a small note when it differs.
  const { formattedPrice, priceTitle } = priceData(livePrice, config.currency, intl);
  const originalDisplayPrice = getDisplayPrice(intl, publicData, config.currency);

  // Listings without a price (e.g. ad-style service provider listings) should
  // not show the "+Kunstavgift" suffix baked into ListingCard.price.
  if (!formattedPrice) {
    return null;
  }

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
      {originalDisplayPrice ? (
        <span className={css.originalCurrencyNote}>
          <FormattedMessage
            id="ListingCard.originalPrice"
            values={{ price: originalDisplayPrice.formattedPrice }}
          />
        </span>
      ) : null}
    </div>
  );
};

/**
 * ReservedBadge
 * Shows a "Reserved" badge on the listing card
 */
const ReservedBadge = ({ intl }) => (
  <div className={css.reservedBadge}>
    <FormattedMessage id="ListingCard.reserved" />
  </div>
);

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @param {boolean} props.isReserved whether the listing is reserved
 * @param {Object} props.intl intl object for translations
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
    isReserved,
    intl,
  } = props;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  // Use scaled variants (non-cropped) for object-fit: contain display
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith('scaled-'))
    : [];

  // Render the listing image only if listing images are enabled in the listing type
  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
      {isReserved && <ReservedBadge intl={intl} />}
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;

  const { listingType, cardStyle, tittel: artworkTitle } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  // Check if listing is reserved
  const isReserved = publicData?.isReserved === true;

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <ListingCardImage
        renderSizes={renderSizes}
        title={title}
        currentListing={currentListing}
        config={config}
        setActivePropsMaybe={setActivePropsMaybe}
        aspectWidth={aspectWidth}
        aspectHeight={aspectHeight}
        variantPrefix={variantPrefix}
        style={cardStyle}
        showListingImage={showListingImage}
        isReserved={isReserved}
        intl={intl}
      />
      <div className={css.info}>
        <div className={css.mainInfo}>
          {showListingImage && (
            <>
              <div className={css.title}>
                {richText(title, {
                  longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                  longWordClass: css.longWord,
                })}
              </div>
              {artworkTitle && (
                <div className={css.artworkTitle}>
                  {richText(artworkTitle, {
                    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                    longWordClass: css.longWord,
                  })}
                </div>
              )}
            </>
          )}
          {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null}
        </div>
        <PriceMaybe
          listing={currentListing}
          price={price}
          publicData={publicData}
          config={config}
          intl={intl}
          listingTypeConfig={foundListingTypeConfig}
        />
      </div>
    </NamedLink>
  );
};

export default ListingCard;
